import type { Point } from '../types/study';

export type ChartBox = {
  width: number;
  height: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type ChartGeometry = {
  box: ChartBox;
  compact: boolean;
  x: (week: number) => number;
  y: (ml: number) => number;
  smooth: (points: Point[]) => string;
  area: (upper: Point[], lower: Point[]) => string;
};

export const desktopBox: ChartBox = { width: 760, height: 380, left: 52, right: 170, top: 28, bottom: 40 };
export const compactBox: ChartBox = { width: 360, height: 320, left: 34, right: 100, top: 28, bottom: 34 };

export const Y_MIN = -200;
export const Y_MAX = 0;
export const WEEKS = 52;

export function createChartGeometry(box: ChartBox, compact: boolean): ChartGeometry {
  const plotW = box.width - box.left - box.right;
  const plotH = box.height - box.top - box.bottom;
  const x = (week: number) => box.left + (week / WEEKS) * plotW;
  const y = (ml: number) => box.top + ((Y_MAX - ml) / (Y_MAX - Y_MIN)) * plotH;

  const smooth = (points: Point[]) => {
    const p = points.map((pt) => ({ x: x(pt.week), y: y(pt.ml) }));
    let d = `M ${p[0].x} ${p[0].y}`;
    for (let i = 0; i < p.length - 1; i++) {
      const p0 = p[i - 1] ?? p[i];
      const p1 = p[i];
      const p2 = p[i + 1];
      const p3 = p[i + 2] ?? p2;
      d += ` C ${p1.x + (p2.x - p0.x) / 6} ${p1.y + (p2.y - p0.y) / 6}, ${p2.x - (p3.x - p1.x) / 6} ${
        p2.y - (p3.y - p1.y) / 6
      }, ${p2.x} ${p2.y}`;
    }
    return d;
  };

  const area = (upper: Point[], lower: Point[]) =>
    `${smooth(upper)} ${smooth([...lower].reverse()).replace(/^M/, 'L')} Z`;

  return { box, compact, x, y, smooth, area };
}

export function estimateTrajectory(base: Point[], difference: number): Point[] {
  return base.map((pt) => ({ week: pt.week, ml: pt.ml + difference * (pt.week / WEEKS) }));
}

export function formatMl(value: number): string {
  return `${value < 0 ? '−' : ''}${Math.abs(value).toFixed(1)}`;
}
